import { Box, Button, Chip, Paper, Tooltip } from '@mui/material';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { navigateToAgentPhoneScreen } from '../../services/agentPhone/agentPhoneBridge';
import { pttRadioLabel } from '../../features/agent-phone/utils/pttLabels';
import { useAgentPhoneCallState } from '../../hooks/useAgentPhoneCallState';
import { usePtt } from '../../contexts/PttContext';
import { useAuth } from '../../shared/contexts/AuthContext';
import { Z_INDEX } from '../../constants/zIndex';
import { TOP_BAR_HEIGHT } from '../layout/navConfig';
import { PttHoldButton } from '../ptt/PttHoldButton';
import { useMemo } from 'react';

/**
 * Active-call / radio overlay only. Idle open control lives in the top bar.
 */
const AgentPhoneDock = () => {
  const { user } = useAuth();
  const callState = useAgentPhoneCallState();
  const onCall = callState.status !== 'idle';
  const {
    remoteSpeaker,
    isReceiving,
    isTransmitting,
    cutIncomingWalkieTalkie,
    startTransmit,
    stopTransmit,
  } = usePtt();
  const onPtt = isReceiving && Boolean(remoteSpeaker);

  const agentDisplayName = useMemo(() => {
    const first = user?.firstName?.trim() || '';
    const last = user?.lastName?.trim() || '';
    const full = `${first} ${last}`.trim();
    return full || user?.username || 'Admin';
  }, [user?.firstName, user?.lastName, user?.username]);

  const replyChannelId = remoteSpeaker?.channel_id ?? null;

  if (!onPtt && !onCall) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: TOP_BAR_HEIGHT,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: Z_INDEX.AGENT_STATUS_BAR,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media print': { display: 'none' },
      }}
    >
      <Paper
        elevation={4}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          justifyContent: 'center',
          rowGap: 1,
          px: 3,
          py: 1.5,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(15px)',
          borderRadius: '0 0 20px 20px',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderTop: 'none',
        }}
      >
        {onPtt && (
          <>
            <Chip
              icon={<MicIcon />}
              label={pttRadioLabel(remoteSpeaker?.display_name)}
              size="small"
              color="success"
              onClick={() => navigateToAgentPhoneScreen()}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
            <PttHoldButton
              isReply
              isTransmitting={isTransmitting}
              disabled={!replyChannelId}
              onPressStart={() => {
                if (replyChannelId) void startTransmit(replyChannelId, agentDisplayName);
              }}
              onPressEnd={() => stopTransmit()}
            />
            <Tooltip title="Stop their transmission">
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => cutIncomingWalkieTalkie(agentDisplayName)}
                startIcon={<StopCircleIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '16px' }}
              >
                Stop
              </Button>
            </Tooltip>
          </>
        )}
        {onCall && (
          <Chip
            icon={<PhoneInTalkIcon />}
            label={
              callState.clientName && callState.clientName !== 'Caller'
                ? `On call: ${callState.clientName}`
                : 'On call'
            }
            size="small"
            color="primary"
            onClick={() => navigateToAgentPhoneScreen()}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default AgentPhoneDock;
